import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    Add as AddIcon,
    CheckCircle as CheckCircleIcon,
    Block as BlockIcon,
    Info as InfoIcon,
    History as HistoryIcon,
    LockOpen as LockOpenIcon,
    MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import { Fab, Menu, MenuItem, useTheme } from '@mui/material';
import { useApi } from '../../../api';
import { Authorized } from '../../providers/user-permission-provider';
import { Breadcrumbs } from '../shared';
import { Permissions } from '../../constants/permissions';

const getStatusColor = (status: EncryptionKeyStatus): 'success' | 'primary' | 'warning' | 'default' => {
    switch (status) {
        case 'Active': return 'success';
        case 'Created': return 'primary';
        case 'Deprecated': return 'warning';
        case 'Retired': return 'default';
        default: return 'default';
    }
};

const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
};

export const EncryptionKeysPage: React.FC = () => {
    const { Api } = useApi();
    const navigate = useNavigate();
    const theme = useTheme();
    const [keys, setKeys] = useState<EncryptionKey[]>([]);
    const [loading, setLoading] = useState(true);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [newKeyDescription, setNewKeyDescription] = useState('');
    const [selectedKey, setSelectedKey] = useState<number | null>(null);
    const [keyStats, setKeyStats] = useState<Record<number, EncryptionKeyStats>>({});
    const [migrateDialogOpen, setMigrateDialogOpen] = useState(false);
    const [migrateBatchSize, setMigrateBatchSize] = useState<number>(100);
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

    const fetchKeyStats = async (keysData: EncryptionKey[]) => {
        const stats: Record<number, EncryptionKeyStats> = {};
        for (const key of keysData) {
            try {
                const stat = await Api.EncryptionKey.getEncryptionKeyStats(key.kid);
                stats[key.kid] = stat;
            } catch (err) {
                console.error(`Failed to load stats for key ${key.kid}`, err);
            }
        }
        return stats;
    };

    // State is only set in promise callbacks so the mount effect doesn't trigger a cascading render
    const loadKeys = () =>
        Api.EncryptionKey.getEncryptionKeys()
            .then(keysData => {
                setKeys(keysData);
                return fetchKeyStats(keysData);
            })
            .then(setKeyStats)
            .catch(error => console.error('Failed to load encryption keys', error))
            .finally(() => setLoading(false));

    useEffect(() => {
        loadKeys();
    }, []);

    const handleCreateKey = async () => {
        try {
            await Api.EncryptionKey.createEncryptionKey({
                description: newKeyDescription || undefined,
            });
            setCreateDialogOpen(false);
            setNewKeyDescription('');
            loadKeys();
        } catch (error) {
            console.error('Failed to create encryption key', error);
        }
    };

    const handleActivateKey = async (kid: number, autoRotate: boolean) => {
        try {
            await Api.EncryptionKey.activateEncryptionKey(kid, autoRotate);
            loadKeys();
        } catch (error) {
            console.error('Failed to activate encryption key', error);
        }
    };

    const handleRetireKey = async (kid: number) => {
        try {
            await Api.EncryptionKey.retireEncryptionKey(kid);
            loadKeys();
        } catch (error) {
            console.error('Failed to retire encryption key', error);
        }
    };

    const handleMigrateUnencrypted = async () => {
        try {
            const activeKey = keys.find(k => k.status === 'Active');
            if (!activeKey) {
                console.error('No active key found');
                return;
            }

            // Start migration using fromKeyId=null to indicate unencrypted images
            await Api.EncryptionKey.startKeyRotation({
                fromKeyId: null,
                toKeyId: activeKey.kid,
                batchSize: migrateBatchSize,
            });

            setMigrateDialogOpen(false);
            
            // Navigate to rotations page to see progress
            navigate(`/encryption-keys/rotations`);
        } catch (error) {
            console.error('Failed to start migration', error);
        }
    };

    if (loading) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "400px"
                }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{
            p: 3
        }}>
            <Breadcrumbs 
                items={[]} 
                homeLabel="Encryption Keys"
                homePath="/encryption-keys"
            />
            <Box sx={{
                position: "relative"
            }}>
                <Authorized permissions={Permissions.Write_EncryptionKeys}>
                    <Box
                        sx={{
                            position: "absolute",
                            right: theme.spacing(1),
                            top: theme.spacing(1),
                            zIndex: 1
                        }}>
                        <Tooltip title="Create New Key" placement="left">
                            <Fab
                                color="primary"
                                aria-label="create new key"
                                onClick={() => setCreateDialogOpen(true)}
                            >
                                <AddIcon />
                            </Fab>
                        </Tooltip>
                    </Box>
                </Authorized>

                <Paper>
                    <Box sx={{
                        position: "relative"
                    }}>
                        <Box
                            sx={{
                                margin: 1,
                                textAlign: "center",
                                position: "relative",

                                // Extra horizontal padding on mobile to avoid menu button overlap
                                px: { xs: 6, sm: 2 },

                                // Slightly more top padding on mobile
                                pt: { xs: 1.5, sm: 1 }
                            }}>
                            <IconButton
                                aria-label="encryption settings"
                                title="Encryption Settings"
                                onClick={(e) => setMenuAnchor(e.currentTarget)}
                                sx={{
                                    position: 'absolute',
                                    left: theme.spacing(1),
                                    top: theme.spacing(1)
                                }}
                            >
                                <MoreVertIcon />
                            </IconButton>
                            
                            <Typography variant='h4' sx={{ 
                                fontSize: { xs: '1.75rem', sm: '2.125rem' } // Slightly smaller on mobile
                            }}>
                                Encryption Key Management
                            </Typography>
                        </Box>

                        <Menu
                            anchorEl={menuAnchor}
                            open={Boolean(menuAnchor)}
                            onClose={() => setMenuAnchor(null)}
                        >
                            <Authorized permissions={Permissions.Read_EncryptionKeys}>
                                <MenuItem onClick={() => {
                                    setMenuAnchor(null);
                                    navigate('/encryption-keys/rotations');
                                }}>
                                    <HistoryIcon sx={{ mr: 1 }} fontSize="small" />
                                    View Rotations
                                </MenuItem>
                            </Authorized>
                            <Authorized permissions={Permissions.Write_EncryptionKeys}>
                                <MenuItem onClick={() => {
                                    setMenuAnchor(null);
                                    setMigrateDialogOpen(true);
                                }}>
                                    <LockOpenIcon sx={{ mr: 1 }} fontSize="small" />
                                    Migrate Unencrypted Images
                                </MenuItem>
                            </Authorized>
                        </Menu>
                    </Box>

                    <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Kid</TableCell>
                            <TableCell>Version</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell>Images</TableCell>
                            <TableCell>Created</TableCell>
                            <TableCell>Activated</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {keys.map((key) => (
                            <TableRow 
                                key={key.kid}
                                sx={{
                                    '&:last-child td, &:last-child th': { border: 0 }
                                }}
                            >
                                <TableCell>{key.kid}</TableCell>
                                <TableCell>v{key.version}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={key.status}
                                        color={getStatusColor(key.status)}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>{key.description || '-'}</TableCell>
                                <TableCell>
                                    {keyStats[key.kid]?.imageCount ?? <CircularProgress size={16} />}
                                </TableCell>
                                <TableCell>{formatDate(key.createdAt)}</TableCell>
                                <TableCell>{formatDate(key.activatedAt)}</TableCell>
                                <TableCell align="right">
                                    <Authorized permissions={Permissions.Write_EncryptionKeys}>
                                        {key.status === 'Created' && (
                                            <>
                                                <Tooltip title="Activate (with auto-rotation)">
                                                    <IconButton
                                                        size="small"
                                                        color="success"
                                                        onClick={() => handleActivateKey(key.kid, true)}
                                                    >
                                                        <CheckCircleIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Activate (without rotation)">
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => handleActivateKey(key.kid, false)}
                                                    >
                                                        <CheckCircleIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </>
                                        )}
                                        {(key.status === 'Active' || key.status === 'Deprecated') && (
                                            <Tooltip title="Retire Key">
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleRetireKey(key.kid)}
                                                >
                                                    <BlockIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Authorized>
                                    <Authorized permissions={Permissions.Read_EncryptionKeys}>
                                        <Tooltip title="View Stats">
                                            <IconButton
                                                size="small"
                                                onClick={() => setSelectedKey(key.kid)}
                                            >
                                                <InfoIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Authorized>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
                </Paper>
            </Box>
            {/* Create Key Dialog */}
            <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)}>
                <DialogTitle>Create New Encryption Key</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Description (optional)"
                        fullWidth
                        value={newKeyDescription}
                        onChange={(e) => setNewKeyDescription(e.target.value)}
                        placeholder="e.g., Production encryption key 2025-Q1"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleCreateKey} variant="contained">
                        Create
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Key Stats Dialog */}
            {selectedKey && keyStats[selectedKey] && (
                <Dialog open={true} onClose={() => setSelectedKey(null)} maxWidth="sm" fullWidth>
                    <DialogTitle>Encryption Key Statistics</DialogTitle>
                    <DialogContent>
                        <Stack spacing={2} sx={{ mt: 1 }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    gap: 2
                                }}>
                                <Box sx={{
                                    flex: 1
                                }}>
                                    <Typography variant="subtitle2" color="textSecondary">
                                        Kid
                                    </Typography>
                                    <Typography variant="h6">{keyStats[selectedKey].kid}</Typography>
                                </Box>
                                <Box sx={{
                                    flex: 1
                                }}>
                                    <Typography variant="subtitle2" color="textSecondary">
                                        Version
                                    </Typography>
                                    <Typography variant="h6">v{keyStats[selectedKey].version}</Typography>
                                </Box>
                            </Box>
                            <Box
                                sx={{
                                    display: "flex",
                                    gap: 2
                                }}>
                                <Box sx={{
                                    flex: 1
                                }}>
                                    <Typography variant="subtitle2" color="textSecondary">
                                        Status
                                    </Typography>
                                    <Chip
                                        label={keyStats[selectedKey].status}
                                        color={getStatusColor(keyStats[selectedKey].status)}
                                    />
                                </Box>
                                <Box sx={{
                                    flex: 1
                                }}>
                                    <Typography variant="subtitle2" color="textSecondary">
                                        Images Encrypted
                                    </Typography>
                                    <Typography variant="h6">{keyStats[selectedKey].imageCount}</Typography>
                                </Box>
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Total Size
                                </Typography>
                                <Typography variant="h6">
                                    {(keyStats[selectedKey].totalSizeBytes / 1024 / 1024).toFixed(2)} MB
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Created At
                                </Typography>
                                <Typography>{formatDate(keyStats[selectedKey].createdAt)}</Typography>
                            </Box>
                            {keyStats[selectedKey].activatedAt && (
                                <Box>
                                    <Typography variant="subtitle2" color="textSecondary">
                                        Activated At
                                    </Typography>
                                    <Typography>{formatDate(keyStats[selectedKey].activatedAt)}</Typography>
                                </Box>
                            )}
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setSelectedKey(null)}>Close</Button>
                    </DialogActions>
                </Dialog>
            )}
            {/* Migrate Unencrypted Images Dialog */}
            <Dialog open={migrateDialogOpen} onClose={() => setMigrateDialogOpen(false)}>
                <DialogTitle>Migrate Unencrypted Images</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1, minWidth: 400 }}>
                        <Typography variant="body2" color="textSecondary">
                            This will encrypt all unencrypted images in the database using the currently active encryption key.
                            The process will run in the background and you can monitor progress on the Rotations page.
                        </Typography>
                        <TextField
                            label="Batch Size"
                            type="number"
                            fullWidth
                            value={migrateBatchSize}
                            onChange={(e) => setMigrateBatchSize(parseInt(e.target.value))}
                            helperText="Number of images to process per batch"
                        />
                        {keys.find(k => k.status === 'Active') && (
                            <Box sx={{ bgcolor: 'info.light', p: 2, borderRadius: 1 }}>
                                <Typography variant="subtitle2">
                                    Target Key: kid {keys.find(k => k.status === 'Active')!.kid} - v{keys.find(k => k.status === 'Active')!.version}
                                </Typography>
                            </Box>
                        )}
                        {!keys.find(k => k.status === 'Active') && (
                            <Box sx={{ bgcolor: 'warning.light', p: 2, borderRadius: 1 }}>
                                <Typography variant="subtitle2" sx={{
                                    color: "warning.dark"
                                }}>
                                    No active encryption key found. Please activate a key first.
                                </Typography>
                            </Box>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setMigrateDialogOpen(false)}>Cancel</Button>
                    <Button 
                        onClick={handleMigrateUnencrypted} 
                        variant="contained"
                        disabled={!keys.find(k => k.status === 'Active')}
                    >
                        Start Migration
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

